import { FirebaseError } from 'firebase/app';
import type { AuthErrorCodes } from 'firebase/auth';

type FirebaseAuthErrorCode =
  (typeof AuthErrorCodes)[keyof typeof AuthErrorCodes];
type SupportedAuthErrorCode = FirebaseAuthErrorCode | 'auth/access-denied';

const AUTH_ERROR_MESSAGES = {
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Please choose a password with at least 6 characters.',
  'auth/wrong-password':
    "Your password doesn't seem correct. Please try again.",
  'auth/user-not-found': "Your email doesn't seem correct. Please try again.",
  'auth/too-many-requests':
    'Your account is temporarily blocked. Please try again later.',
  'auth/access-denied': 'You are not authorized to access.',
  'auth/account-exists-with-different-credential':
    'You have signed in with the wrong method for your account.',
  'auth/email-already-in-use':
    "We couldn't create your account. Try signing in or use a different email address.",
} satisfies Partial<Record<SupportedAuthErrorCode, string>>;

type AuthErrorCode = keyof typeof AUTH_ERROR_MESSAGES;

interface ResolvedAuthError {
  code?: string;
  message: string;
}

const getAuthErrorCode = (err: unknown): string | undefined => {
  if (err instanceof FirebaseError) return err.code;
  return undefined;
};

const isAuthErrorCode = (code: string): code is AuthErrorCode =>
  code in AUTH_ERROR_MESSAGES;

const resolveAuthError = (
  err: unknown,
  fallback: string = 'Please try again later or contact Guuru Support',
): ResolvedAuthError => {
  const code = getAuthErrorCode(err);
  if (code) {
    return {
      code,
      message: isAuthErrorCode(code) ? AUTH_ERROR_MESSAGES[code] : fallback,
    };
  }
  if (err instanceof Error) return { message: err.message };
  if (typeof err === 'string') return { message: err };
  return { message: fallback };
};

export { AUTH_ERROR_MESSAGES, resolveAuthError };
